import { useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { SaveStatus } from "@/components/SaveStatus";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { refusal } from "@/lib/api/client";
import { useAutosave } from "@/lib/hooks/useAutosave";

import { cn } from "@/lib/utils/cn";
import { thematicClasses } from "@/lib/thematic";

import {
  saveActionName,
  saveCustomAction,
  useAvailableActions,
  useAvailableGoals,
  useCreateCustomAction,
  useCreateGoal,
  useCustomActionsQuery,
  useDeleteAction,
  useDeleteCustomAction,
  useDeleteGoal,
  useRenameGoal,
  useSelectAction,
  type AvailableAction,
  type AvailableGoal,
} from "../api";
import type { CustomActionToCommunicate } from "../types";

/** Goal header with inline rename (GoalCustomization) and, for custom goals, delete. */
export function GoalHeader({
  goalId,
  name,
  isCustom,
  memberId,
}: {
  goalId: string;
  name: string;
  isCustom: boolean;
  /** The reviewed member's profile id, when a validator edits on review. */
  memberId?: string;
}) {
  const { t } = useTranslation();
  const rename = useRenameGoal(memberId);
  const remove = useDeleteGoal(memberId);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);

  const save = () => {
    rename.mutate(
      { id: goalId, customName: value.trim() },
      {
        onSuccess: () => setEditing(false),
        // The service refuses a goal left with no action, and says so.
        onError: (err) =>
          toast.error(refusal(err) ?? t("communications.actions.saveError")),
      },
    );
  };

  if (editing) {
    return (
      <div className="flex items-center gap-2 bg-muted/40 px-4 py-2">
        <Input
          value={value}
          maxLength={500}
          autoFocus
          onChange={(e) => setValue(e.target.value)}
          placeholder={name}
          className="h-8"
        />
        <Button size="sm" onClick={save} disabled={rename.isPending}>
          {t("communications.actions.renameSave")}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setValue(name);
            setEditing(false);
          }}
        >
          {t("communications.actions.renameCancel")}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-2 bg-muted/40 px-4 py-1.5">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {name}
      </p>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setValue(name);
            setEditing(true);
          }}
          aria-label={t("communications.actions.renameGoal")}
        >
          <Pencil aria-hidden />
        </Button>
        {isCustom && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              remove.mutate(goalId, {
                onError: (err) =>
                  toast.error(
                    refusal(err) ?? t("communications.actions.saveError"),
                  ),
              })
            }
            disabled={remove.isPending}
            aria-label={t("communications.actions.deleteGoal")}
          >
            {remove.isPending ? (
              t("communications.actions.deleting")
            ) : (
              <Trash2 aria-hidden />
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

/** Add a member-authored custom action under a specific goal. */
function AddCustomAction({
  goalId,
  memberId,
}: {
  goalId: string;
  memberId?: string;
}) {
  const { t } = useTranslation();
  const create = useCreateCustomAction(memberId);
  const [description, setDescription] = useState("");

  const submit = () => {
    if (description.trim() === "") return;
    create.mutate(
      { goal: goalId, description: description.trim() },
      { onSuccess: () => setDescription("") },
    );
  };

  return (
    <div className="flex items-end gap-2 px-4 py-3">
      <div className="flex-1 space-y-1.5">
        <Label htmlFor={`add-action-${goalId}`}>
          {t("communications.actions.customCreateLabel")}
        </Label>
        <Input
          id={`add-action-${goalId}`}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      <Button
        type="button"
        variant="outline"
        onClick={submit}
        disabled={description.trim() === "" || create.isPending}
      >
        <Plus aria-hidden />
        {t("communications.actions.customAdd")}
      </Button>
    </div>
  );
}

/** Create a brand-new custom goal under an issue. */
export function CreateCustomGoal({
  issueId,
  memberId,
}: {
  issueId: string;
  /** The reviewed member's profile id, when a validator edits on review. */
  memberId?: string;
}) {
  const { t } = useTranslation();
  const create = useCreateGoal(memberId);
  const [name, setName] = useState("");
  const [firstAction, setFirstAction] = useState("");
  // A goal is created with its first action: until both are filled, say why
  // the button waits.
  const missingAction = name.trim() !== "" && firstAction.trim() === "";

  const submit = () => {
    if (name.trim() === "" || firstAction.trim() === "") return;
    create.mutate(
      { issue: issueId, name: name.trim(), first_action: firstAction.trim() },
      {
        onSuccess: () => {
          setName("");
          setFirstAction("");
        },
        onError: (err) =>
          toast.error(refusal(err) ?? t("communications.actions.saveError")),
      },
    );
  };

  return (
    <div className="space-y-2 border-t bg-muted/20 px-4 py-3">
      <div className="flex items-end gap-2">
        <div className="flex-1 space-y-1.5">
          <Label htmlFor={`new-goal-${issueId}`}>
            {t("communications.actions.newGoalLabel")}
          </Label>
          <Input
            id={`new-goal-${issueId}`}
            value={name}
            maxLength={200}
            placeholder={t("communications.actions.newGoalPlaceholder")}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="flex-1 space-y-1.5">
          <Label htmlFor={`new-goal-action-${issueId}`}>
            {t("communications.actions.newGoalFirstActionLabel")}
          </Label>
          <Input
            id={`new-goal-action-${issueId}`}
            value={firstAction}
            maxLength={400}
            onChange={(e) => setFirstAction(e.target.value)}
          />
        </div>
        <Button
          type="button"
          onClick={submit}
          disabled={
            name.trim() === "" || firstAction.trim() === "" || create.isPending
          }
        >
          <Plus aria-hidden />
          {create.isPending
            ? t("communications.actions.saving")
            : t("communications.actions.newGoalAdd")}
        </Button>
      </div>
      {missingAction && (
        <p className="text-xs text-muted-foreground">
          {t("communications.actions.goalNeedsAction")}
        </p>
      )}
    </div>
  );
}

/**
 * An available carried-out action: a checkbox features it on the showcase, and
 * once featured the member can override its display label (custom_name) —
 * autosaved. Mirrors the legacy actions-selection choice + rename.
 */
function AvailableActionRow({
  action,
  memberId,
}: {
  action: AvailableAction;
  memberId?: string;
}) {
  const { t } = useTranslation();
  const select = useSelectAction(memberId);
  const remove = useDeleteAction(memberId);
  const [value, setValue] = useState(action.custom_name ?? "");

  const autosave = useAutosave<string>({
    save: (v, signal) => saveActionName(action.communicate_id ?? "", v, signal, memberId),
    debounceMs: 600,
  });

  const toggle = (on: boolean) => {
    if (on)
      select.mutate({
        actionId: action.action_id,
        issueChoiceId: action.issue_choice_id,
      });
    else if (action.communicate_id)
      remove.mutate(action.communicate_id, {
        // An objective that features an action keeps one: the service
        // refuses unfeaturing its last, and says so.
        onError: (err) =>
          toast.error(refusal(err) ?? t("communications.actions.saveError")),
      });
  };

  return (
    <div className="space-y-2 px-4 py-3">
      <label className="flex items-start gap-2.5 text-sm">
        <input
          type="checkbox"
          className="mt-0.5 size-4 accent-primary"
          checked={action.is_selected}
          disabled={select.isPending || remove.isPending}
          onChange={(e) => toggle(e.target.checked)}
        />
        <span className="flex-1 font-medium">{action.action_name}</span>
        {action.is_selected && <SaveStatus status={autosave.status} />}
      </label>
      {action.is_selected && action.communicate_id && (
        <div className="space-y-1.5 pl-[26px]">
          <Label htmlFor={`action-${action.communicate_id}`}>
            {t("communications.actions.displayLabel")}
          </Label>
          <Input
            id={`action-${action.communicate_id}`}
            value={value}
            placeholder={action.action_name}
            onChange={(e) => {
              setValue(e.target.value);
              autosave.save(e.target.value);
            }}
          />
          {action.comment && (
            <p className="text-xs text-muted-foreground">
              {t("communications.actions.answerComment", {
                comment: action.comment,
              })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

interface CustomActionState {
  description: string;
  comment: string;
  selected: boolean;
  show_comment_publicly: boolean;
}

/** A member-authored action: editable text + visibility toggles, all autosaved. */
function CustomActionRow({
  item,
  memberId,
}: {
  item: CustomActionToCommunicate;
  memberId?: string;
}) {
  const { t } = useTranslation();
  const remove = useDeleteCustomAction(memberId);
  const [state, setState] = useState<CustomActionState>({
    description: item.description,
    comment: item.comment ?? "",
    selected: item.selected ?? true,
    show_comment_publicly: item.show_comment_publicly ?? false,
  });

  const autosave = useAutosave<CustomActionState>({
    save: (v, signal) => saveCustomAction(item.id, v, signal, memberId),
    debounceMs: 600,
    shouldSave: (v) => v.description.trim() !== "",
  });

  function update(patch: Partial<CustomActionState>) {
    const next = { ...state, ...patch };
    setState(next);
    autosave.save(next);
  }

  // Featuring is saved at once, not autosaved: the service refuses
  // unfeaturing an objective's last action, and the box has to go back.
  function toggleSelected(selected: boolean) {
    const next = { ...state, selected };
    setState(next);
    autosave.cancel();
    saveCustomAction(item.id, next, undefined, memberId).catch((err: unknown) => {
      setState((current) => ({ ...current, selected: !selected }));
      toast.error(refusal(err) ?? t("communications.actions.saveError"));
    });
  }

  return (
    <div className="space-y-3 px-4 py-3">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={`custom-${item.id}`}>
          {t("communications.actions.customLabel")}
        </Label>
        <div className="flex items-center gap-2">
          <SaveStatus status={autosave.status} />
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              remove.mutate(item.id, {
                onError: (err) =>
                  toast.error(
                    refusal(err) ?? t("communications.actions.saveError"),
                  ),
              })
            }
            disabled={remove.isPending}
            aria-label={t("communications.actions.removeCustom")}
          >
            {remove.isPending ? (
              t("communications.actions.deleting")
            ) : (
              <Trash2 aria-hidden />
            )}
          </Button>
        </div>
      </div>
      <Input
        id={`custom-${item.id}`}
        value={state.description}
        aria-invalid={state.description.trim() === ""}
        onChange={(e) => update({ description: e.target.value })}
      />
      <div className="space-y-1.5">
        <Label htmlFor={`comment-${item.id}`}>
          {t("communications.actions.comment")}
        </Label>
        <textarea
          id={`comment-${item.id}`}
          rows={2}
          value={state.comment}
          onChange={(e) => update({ comment: e.target.value })}
          className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-base focus-visible:outline-none md:text-sm"
        />
      </div>
      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="size-4 rounded border-input"
            checked={state.selected}
            onChange={(e) => toggleSelected(e.target.checked)}
          />
          {t("communications.actions.selected")}
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="size-4 rounded border-input"
            checked={state.show_comment_publicly}
            onChange={(e) => update({ show_comment_publicly: e.target.checked })}
          />
          {t("communications.actions.showComment")}
        </label>
      </div>
    </div>
  );
}

interface GoalBlock {
  goalId: string;
  name: string;
  isCustom: boolean;
  actions: AvailableAction[];
  customActions: CustomActionToCommunicate[];
}
interface IssueBlock {
  issueId: string;
  issueName: string;
  cssClass: string;
  goals: GoalBlock[];
}

/**
 * Build issue → goal blocks from the goals list (ordered), attaching each goal's
 * available actions and member-authored custom actions. Mirrors the legacy
 * actions-selection view iterating issue.goals → actions + custom_actions.
 */
function buildGroups(
  goals: AvailableGoal[],
  actions: AvailableAction[],
  custom: CustomActionToCommunicate[],
): IssueBlock[] {
  const actionsByGoal = new Map<string, AvailableAction[]>();
  for (const a of actions) {
    const list = actionsByGoal.get(a.goal_id) ?? [];
    list.push(a);
    actionsByGoal.set(a.goal_id, list);
  }
  const customByGoal = new Map<string, CustomActionToCommunicate[]>();
  for (const c of custom) {
    const gid = c.goal;
    const list = customByGoal.get(gid) ?? [];
    list.push(c);
    customByGoal.set(gid, list);
  }
  const issues = new Map<string, IssueBlock>();
  for (const g of goals) {
    let issue = issues.get(g.issue_id);
    if (!issue) {
      issue = {
        issueId: g.issue_id,
        issueName: g.issue_name,
        cssClass: g.css_class,
        goals: [],
      };
      issues.set(g.issue_id, issue);
    }
    issue.goals.push({
      goalId: g.goal_id,
      name: g.name,
      isCustom: g.is_custom,
      actions: actionsByGoal.get(g.goal_id) ?? [],
      customActions: customByGoal.get(g.goal_id) ?? [],
    });
  }
  return [...issues.values()];
}

/**
 * Customize the actions featured on the showcase, grouped by issue → goal.
 * Members can select/relabel diagnostic actions, add/edit/annotate custom
 * actions, rename any goal, and create or delete their own custom goals.
 */
export function ActionsStep({
  memberId,
}: {
  /** The reviewed member's profile id, when a validator edits on review. */
  memberId?: string;
} = {}) {
  const { t } = useTranslation();
  const goalsQ = useAvailableGoals(memberId);
  const actionsQ = useAvailableActions(memberId);
  const customQ = useCustomActionsQuery(memberId);

  const loading = goalsQ.isPending || actionsQ.isPending || customQ.isPending;
  const isError = goalsQ.isError || actionsQ.isError || customQ.isError;

  const groups = buildGroups(
    goalsQ.data ?? [],
    actionsQ.data ?? [],
    customQ.data ?? [],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("communications.actions.title")}</CardTitle>
        <CardDescription>
          {t("communications.actions.description")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-muted-foreground">{t("communications.loading")}</p>
        ) : isError ? (
          <p className="text-destructive">
            {t("communications.actions.loadError")}
          </p>
        ) : groups.length === 0 ? (
          <p className="text-muted-foreground">
            {t("communications.actions.empty")}
          </p>
        ) : (
          <div className="space-y-4">
            {groups.map((issue) => {
              const tc = thematicClasses(issue.cssClass);
              return (
                <section
                  key={issue.issueId}
                  className="overflow-hidden rounded-xl border"
                >
                  <h3
                    className={cn(
                      "px-3 py-2 text-sm font-bold",
                      tc.softBg,
                      tc.ink,
                    )}
                  >
                    {issue.issueName}
                  </h3>
                  {issue.goals.map((goal) => (
                    <div key={goal.goalId} className="border-t">
                      <GoalHeader
                        goalId={goal.goalId}
                        name={goal.name}
                        isCustom={goal.isCustom}
                        memberId={memberId}
                      />
                      <div className="divide-y divide-border">
                        {goal.actions.map((action) => (
                          <AvailableActionRow
                            key={action.action_id}
                            action={action}
                            memberId={memberId}
                          />
                        ))}
                        {goal.customActions.map((item) => (
                          <CustomActionRow
                            key={item.id}
                            item={item}
                            memberId={memberId}
                          />
                        ))}
                      </div>
                      <AddCustomAction goalId={goal.goalId} memberId={memberId} />
                    </div>
                  ))}
                  <CreateCustomGoal issueId={issue.issueId} memberId={memberId} />
                </section>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
